import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { apiWordleSolver } from '../utils/api';
import SeoContent from '../components/SeoContent';
import PageSEO from '../components/PageSEO';

const STATES = ['empty', 'absent', 'present', 'correct'];
const STATE_COLORS = { empty:'#fff', absent:'#6b7280', present:'#f59e0b', correct:'#10b981' };
const STATE_LABELS = { empty:'Unknown', absent:'Not in word (gray)', present:'Wrong position (yellow)', correct:'Right position (green)' };

function initRow() {
  return Array.from({length:5}, () => ({letter:'', state:'empty'}));
}

export default function WordleSolver() {
  const [rows, setRows]       = useState([initRow()]);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');

  function updateLetter(rowIdx, colIdx, val) {
    const r = rows.map(row => row.map(cell => ({...cell})));
    r[rowIdx][colIdx].letter = val.replace(/[^a-zA-Z]/g,'').toUpperCase().slice(-1);
    if (r[rowIdx][colIdx].letter && r[rowIdx][colIdx].state === 'empty') {
      r[rowIdx][colIdx].state = 'absent';
    }
    setRows(r);
  }

  function cycleState(rowIdx, colIdx) {
    const r = rows.map(row => row.map(cell => ({...cell})));
    if (!r[rowIdx][colIdx].letter) return;
    const cur = STATES.indexOf(r[rowIdx][colIdx].state);
    r[rowIdx][colIdx].state = STATES[(cur + 1) % STATES.length === 0 ? 1 : (cur + 1) % STATES.length];
    setRows(r);
  }

  function addRow() {
    if (rows.length < 6) setRows([...rows, initRow()]);
  }

  async function handleSolve(e) {
    e.preventDefault();
    setLoading(true); setError(''); setResults(null);
    const correct = {};
    const present = {};
    const absent  = new Set();
    rows.forEach(row => {
      row.forEach((cell, i) => {
        if (!cell.letter) return;
        if (cell.state === 'correct') correct[String(i)] = cell.letter.toLowerCase();
        else if (cell.state === 'present') {
          const l = cell.letter.toLowerCase();
          if (!present[l]) present[l] = [];
          present[l].push(String(i));
        } else if (cell.state === 'absent') {
          absent.add(cell.letter.toLowerCase());
        }
      });
    });
    try {
      const { data } = await apiWordleSolver(correct, present, [...absent]);
      setResults(data);
    } catch (err) {
      setError(err.response?.data?.error || 'Something went wrong.');
    } finally { setLoading(false); }
  }

  return (
    <div className="game-page">
      <PageSEO page="wordle-solver" />
      <div className="container" style={{maxWidth:600}}>
        <Link to="/" className="back-btn"><i className="bi bi-arrow-left"></i> All Games</Link>

        <div className="game-header">
          <span className="game-icon">💡</span>
          <h1>Wordle Solver</h1>
          <p>Enter your guesses with color hints to get smart word suggestions!</p>
        </div>

        <div className="solver-card">
          <div className="p-3 mb-3 rounded-3" style={{background:'#ede9fe',border:'1px solid #c4b5fd'}}>
            <p className="fw-bold mb-1" style={{color:'#5b21b6'}}>How it works:</p>
            <div className="d-flex gap-3 flex-wrap">
              {['correct','present','absent'].map(s => (
                <span key={s} className="d-flex align-items-center gap-1 small fw-bold">
                  <span style={{
                    width:20,height:20,borderRadius:4,background:STATE_COLORS[s],
                    display:'inline-block',border:'1px solid #ddd'
                  }}></span>
                  {STATE_LABELS[s].split('(')[0]}
                </span>
              ))}
            </div>
            <p className="mb-0 mt-1 small text-muted fw-bold">Type a letter, then click the cell to cycle its color</p>
          </div>

          <form onSubmit={handleSolve}>
            {rows.map((row, rowIdx) => (
              <div key={rowIdx} className="mb-2">
                <div className="d-flex gap-2 align-items-center justify-content-center">
                  <small className="text-muted fw-bold me-1" style={{minWidth:'2rem'}}>#{rowIdx+1}</small>
                  {row.map((cell, colIdx) => (
                    <div key={colIdx} style={{position:'relative'}}>
                      <input
                        value={cell.letter}
                        onChange={e => updateLetter(rowIdx, colIdx, e.target.value)}
                        style={{
                          width:52, height:52, textAlign:'center', fontWeight:900,
                          fontSize:'1.4rem', textTransform:'uppercase', fontFamily:'Nunito,sans-serif',
                          border:`3px solid ${cell.state === 'empty' ? '#d1d5db' : STATE_COLORS[cell.state]}`,
                          borderRadius:8, background: STATE_COLORS[cell.state],
                          color: cell.state === 'empty' ? 'var(--text)' : '#fff',
                          cursor: cell.letter ? 'pointer' : 'text',
                        }}
                        maxLength={1}
                        onClick={() => cycleState(rowIdx, colIdx)}
                        title={cell.letter ? `Click to cycle: ${STATE_LABELS[cell.state]}` : 'Type a letter'}
                        readOnly={false}
                      />
                    </div>
                  ))}
                </div>
              </div>
            ))}

            {rows.length < 6 && (
              <button type="button" className="btn btn-sm fw-bold mb-3 mt-1" onClick={addRow}
                style={{borderRadius:'50px',background:'var(--border)',border:'none',fontFamily:'Nunito,sans-serif'}}>
                + Add Another Guess
              </button>
            )}

            <div className="d-flex gap-2">
              <button type="submit" className="solve-btn" disabled={loading}>
                {loading ? <><span className="spinner-border spinner-border-sm me-2"></span>Finding…</> : '💡 Find Words'}
              </button>
              <button type="button"
                onClick={() => { setRows([initRow()]); setResults(null); setError(''); }}
                className="btn fw-bold"
                style={{borderRadius:'12px',background:'var(--border)',border:'none',fontFamily:'Nunito,sans-serif'}}
              >Reset</button>
            </div>
          </form>
        </div>

        {error && <div className="alert alert-danger alert-custom">{error}</div>}

        {results && (
          <div className="solver-card">
            <p className="result-count">
              {results.count === 0
                ? 'No matching words found. Check your clues!'
                : <>Found <strong>{results.count}</strong> possible word{results.count !== 1 ? 's' : ''}</>
              }
            </p>
            <div className="words-cloud">
              {results.results?.map(w => (
                <span key={w} className="result-badge" style={{fontSize:'1.05rem', letterSpacing:2}}>
                  {w.toUpperCase()}
                </span>
              ))}
            </div>
          </div>
        )}
      <SeoContent game="wordle-solver" />
      </div>
    </div>
  );
}
